import * as THREE from "three";
import Orchestrator from "../Orchestrator.js";
import { device } from "../Utils/device.js";
import { CONFIG } from "./config.js";
import vertexShader from "./shaders/sky/vertex.glsl";
import fragmentShader from "./shaders/sky/fragment.glsl";
import milkyVertexShader from "./shaders/skyMilkyWay/vertex.glsl";
import milkyFragmentShader from "./shaders/skyMilkyWay/fragment.glsl";

const DAY_CYCLE = 30;
const TAU = Math.PI * 2;
const GALACTIC_TILT = 1.02;

// GLSL smoothstep allows edge0 > edge1. MathUtils.smoothstep does not.
function glslSmoothstep(edge0, edge1, x) {
    const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
    return t * t * (3 - 2 * t);
}

function srgbColor(r, g, b) {
    return new THREE.Color().setRGB(r, g, b, THREE.SRGBColorSpace);
}

export default class Sky {
    constructor() {
        this.orc = new Orchestrator();
        this.camera = this.orc.camera;
        this.debug = this.orc.debug;

        // 0 and 1 are midday, 0.5 is midnight.
        this.params = {
            dayProgress: 0.2,
            animateDay: false,
            atmosphericOpacity: 0.1,
            latitude: 0,
        };
        this.dayProgressController = null;

        this._dayZenith = srgbColor(0.776, 0.42, 0.31);
        this._dayHorizon = srgbColor(0.925, 0.675, 0.455);
        this._setZenith = srgbColor(0.365, 0.18, 0.23);
        this._setHorizon = srgbColor(0.71, 0.365, 0.33);
        this._nightZenith = srgbColor(0.016, 0.014, 0.018);
        this._dayGround = srgbColor(0.42, 0.18, 0.11);
        this._nightGround = srgbColor(0.09, 0.04, 0.028);

        this._zenith = new THREE.Color();
        this._horizon = new THREE.Color();
        this._ground = new THREE.Color();
        this._nightHorizonColor = new THREE.Color();
        this._groundNight = new THREE.Color();

        this._sunDir = new THREE.Vector3();
        this._flatSunDir = new THREE.Vector3();
        this._basis = new THREE.Matrix3();
        this._rotY = new THREE.Matrix3();
        this._tilt = new THREE.Matrix3();
        this._celestial = new THREE.Matrix3();
        this._galactic = new THREE.Matrix3();

        const tiltCos = Math.cos(GALACTIC_TILT);
        const tiltSin = Math.sin(GALACTIC_TILT);
        this._tilt.set(1, 0, 0, 0, tiltCos, -tiltSin, 0, tiltSin, tiltCos);

        this.milkyTarget = this.bakeMilkyWay();

        this.material = new THREE.ShaderMaterial({
            uniforms: {
                uTime: { value: 0 },
                uAtmosphere: { value: this.params.atmosphericOpacity },
                uSunDir: { value: this._sunDir },
                uFlatSunDir: { value: this._flatSunDir },
                uNight: { value: 0 },
                uSunset: { value: 0 },
                uDaylight: { value: 1 },
                uAfterglow: { value: 0 },
                uStarVisibility: { value: 0 },
                uZenith: { value: this._zenith },
                uHorizon: { value: this._horizon },
                uGround: { value: this._ground },
                uDayGlow: { value: srgbColor(1, 0.82, 0.62) },
                uAfterglowColor: { value: srgbColor(0.62, 0.23, 0.16) },
                uSetBlue: { value: srgbColor(0.455, 0.64, 0.8) },
                uNightHorizon: { value: srgbColor(0.553, 0.286, 0.204) },
                uMilkyWarm: { value: srgbColor(0.68, 0.4, 0.34) },
                uMilkyCool: { value: srgbColor(0.78, 0.76, 0.84) },
                uMilkyDust: { value: srgbColor(0.72, 0.32, 0.28) },
                uStarWhite: { value: srgbColor(1, 0.96, 0.9) },
                uStarOrange: { value: srgbColor(1, 0.74, 0.42) },
                uStarBlue: { value: srgbColor(0.62, 0.8, 1) },
                uCelestialMatrix: { value: this._celestial },
                uGalacticMatrix: { value: this._galactic },
                uMilkyMap: { value: this.milkyTarget.texture },
                uStorm: { value: 0 },
                uStormColor: { value: new THREE.Color() },
            },
            vertexShader,
            fragmentShader,
            side: THREE.BackSide,
            depthTest: true,
            depthWrite: false,
            fog: false,
        });

        this.geometry = new THREE.SphereGeometry(CONFIG.CAMERA_FAR * 0.9, 64, 48);
        this.mesh = new THREE.Mesh(this.geometry, this.material);
        this.mesh.name = "sky";
        this.mesh.frustumCulled = false;
        // After the opaque world, so the depth test skips sky behind the terrain.
        this.mesh.renderOrder = 1000;
        this.camera.instance.add(this.mesh);

        this.update(0, 0);
        this.setDebug();
    }

    // The Milky Way noise depends only on galactic direction, so it is rendered
    // once into a cubemap and sampled instead of evaluated per pixel.
    bakeMilkyWay() {
        const size = device.isLowPower ? 256 : 512;
        const target = new THREE.WebGLCubeRenderTarget(size, {
            type: THREE.UnsignedByteType,
            format: THREE.RGBAFormat,
            generateMipmaps: false,
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            colorSpace: THREE.LinearSRGBColorSpace,
            depthBuffer: false,
        });

        const scene = new THREE.Scene();
        const geometry = new THREE.BoxGeometry(2, 2, 2);
        const material = new THREE.ShaderMaterial({
            vertexShader: milkyVertexShader,
            fragmentShader: milkyFragmentShader,
            side: THREE.BackSide,
            depthTest: false,
            depthWrite: false,
        });
        scene.add(new THREE.Mesh(geometry, material));

        const cubeCamera = new THREE.CubeCamera(0.1, 10, target);
        cubeCamera.update(this.orc.renderer.instance, scene);

        geometry.dispose();
        material.dispose();
        return target;
    }

    getHorizonColor() {
        return this._horizon;
    }

    getSunDir() {
        return this._sunDir;
    }

    setStorm(amount, color) {
        this.material.uniforms.uStorm.value = amount;
        this.material.uniforms.uStormColor.value.copy(color);
    }

    // 0 at noon, 1 at midnight. Smoothstep of sun elevation, so it follows dayProgress.
    getNight() {
        return this.material.uniforms.uNight.value;
    }

    setDebug() {
        if (!this.debug.active) return;

        const folder = this.debug.ui.addFolder("Sky");
        this.dayProgressController = folder
            .add(this.params, "dayProgress", 0, 1, 0.001)
            .name("dayProgress");
        folder.add(this.params, "animateDay").name("animate day");
        folder.add(this.params, "latitude", -90, 90, 1).name("latitude");
        folder
            .add(this.params, "atmosphericOpacity", 0, 1, 0.001)
            .name("atmospheric opacity");
    }

    update(dt, elapsed) {
        if (this.params.animateDay) {
            this.params.dayProgress =
                (this.params.dayProgress + dt / DAY_CYCLE) % 1;
            this.dayProgressController?.updateDisplay();
        }

        const angle = this.params.dayProgress * TAU;
        const lat = THREE.MathUtils.degToRad(this.params.latitude);
        const sinAngle = Math.sin(angle);
        const cosAngle = Math.cos(angle);
        const sinLat = Math.sin(lat);
        const cosLat = Math.cos(lat);

        this._sunDir.set(sinAngle, cosLat * cosAngle, sinLat * cosAngle);
        const sunY = this._sunDir.y;
        const night = 1 - glslSmoothstep(-0.7, 0.05, sunY);
        const sunset = Math.exp(-16 * sunY * sunY) * (1 - night);
        const daylight = glslSmoothstep(-0.02, 0.35, sunY);
        const afterglow =
            (1 - glslSmoothstep(-0.02, 0.12, sunY)) *
            glslSmoothstep(-0.6, -0.12, sunY);
        const starVisibility = glslSmoothstep(0.14, -0.5, sunY);

        const atmosphere = this.params.atmosphericOpacity;
        this._zenith
            .copy(this._dayZenith)
            .lerp(this._setZenith, sunset)
            .lerp(this._nightZenith, night);
        this._nightHorizonColor
            .copy(this._nightZenith)
            .lerp(this.material.uniforms.uNightHorizon.value, atmosphere);
        this._horizon
            .copy(this._dayHorizon)
            .lerp(this._setHorizon, sunset)
            .lerp(this._nightHorizonColor, night);
        this._groundNight
            .copy(this._nightZenith)
            .lerp(this._nightGround, atmosphere);
        this._ground.copy(this._dayGround).lerp(this._groundNight, night);

        const flatLen2 =
            this._sunDir.x * this._sunDir.x + this._sunDir.z * this._sunDir.z;
        const flatInv = 1 / Math.sqrt(Math.max(flatLen2, 1e-6));
        this._flatSunDir.set(
            this._sunDir.x * flatInv,
            0,
            this._sunDir.z * flatInv,
        );

        this._basis.set(1, 0, 0, 0, sinLat, -cosLat, 0, cosLat, sinLat);
        const rotSin = Math.sin(-angle);
        const rotCos = Math.cos(-angle);
        this._rotY.set(rotCos, 0, rotSin, 0, 1, 0, -rotSin, 0, rotCos);
        this._celestial.multiplyMatrices(this._rotY, this._basis);
        this._galactic.multiplyMatrices(this._tilt, this._celestial);

        const uniforms = this.material.uniforms;
        uniforms.uTime.value = elapsed;
        uniforms.uAtmosphere.value = atmosphere;
        uniforms.uNight.value = night;
        uniforms.uSunset.value = sunset;
        uniforms.uDaylight.value = daylight;
        uniforms.uAfterglow.value = afterglow;
        uniforms.uStarVisibility.value = starVisibility;
    }
}
